// src/admin/pages/TrainingsPage.jsx
import React, { useState } from "react";
import {
  Box, Button, Card, Typography, Chip, IconButton, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, MenuItem, CircularProgress,
  Skeleton, Snackbar, Alert, InputAdornment, Tooltip, Avatar,
  Table, TableHead, TableRow, TableCell, TableBody, Switch, FormControlLabel,
  TablePagination, Paper, Menu,
} from "@mui/material";
import {
  Plus, Pencil, Trash2, GraduationCap, Type, Clock, Layers, Upload,
  DollarSign, Star, Users, Search, MoreVertical, Download, FileUp, FileDown,
  Link2, ImagePlus, X,
} from "lucide-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useForm, Controller } from "react-hook-form";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import AdminShell from "../layout/AdminShell";
import {
  adminGetTrainings, adminGetTrainingById, adminCreateTraining, adminUpdateTraining,
  adminDeleteTraining, adminSetTrainingStatus,
  adminExportTrainings, adminDownloadTrainingTemplate, adminImportTrainings,
} from "../../api/trainings";
import { downloadBlob } from "../../utils/downloadBlob";

const MODES = [
  { label: "Classroom", value: "classroom" },
  { label: "Live Online", value: "liveonline" },
  { label: "Self-paced", value: "selfpaced" },
  { label: "Custom", value: "custom" },
];

const DEFAULT_VALUES = {
  title: "", slug: "", badge: "", shortDescription: "", description: "",
  imageUrl: "", iconUrl: "", category: "", mode: "classroom", duration: "",
  level: "", price: "", currency: "INR", rating: "", enrollmentCount: "",
  ctaLabel: "Enroll now", ctaUrl: "", featured: false, displayOrder: 0, isActive: true,
};

// Quill's toolbar for the description editor. Kept intentionally small —
// headings/bold/lists/link cover everything TrainingDetailPage needs to render.
const QUILL_MODULES = {
  toolbar: [
    [{ header: [2, 3, false] }],
    ["bold", "italic", "underline"],
    [{ list: "ordered" }, { list: "bullet" }],
    ["link"],
    ["clean"],
  ],
clipboard: {
    // Strip all formatting (inline styles, spans, mso-* junk from Word/Docs
    // pastes) and keep only plain text — the toolbar above is how users
    // apply real formatting instead.
    matchVisual: false,
  },
};
const QUILL_FORMATS = ["header", "bold", "italic", "underline", "list", "bullet", "link"];

// Quill reports an empty editor as "<p><br></p>" instead of "" — normalize
// that to a real empty string so required-checks / payloads stay clean.
const isQuillEmpty = (html) => !html || html.replace(/<(.|\n)*?>/g, "").trim() === "";

const slugify = (str) => str.toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");

export default function TrainingsPage() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [editLoading, setEditLoading] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [importing, setImporting] = useState(false);
  const fileInputRef = React.useRef(null);
  const quillRefs = React.useRef({});

  // Actual binary uploads for the multipart `image` / `icon` fields.
  // Kept separate from react-hook-form since MUI + RHF file inputs are awkward together.
  const [imageFile, setImageFile] = useState(null);
  const [iconFile, setIconFile] = useState(null);
  const imageFileInputRef = React.useRef(null);
  const iconFileInputRef = React.useRef(null);

  const { register, handleSubmit, reset, control, watch, setValue, formState: { errors } } =
    useForm({ defaultValues: DEFAULT_VALUES });

  const notify = (message, severity = "success") => setSnackbar({ open: true, message, severity });

  const { data: apiTrainings = [], isLoading } = useQuery({
    queryKey: ["admin-trainings"],
    queryFn: () => adminGetTrainings().then((r) => r.data?.data ?? r.data),
  });
  const trainings = Array.isArray(apiTrainings) ? apiTrainings : [];
  const filtered = trainings.filter((t) =>
    t.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    t.category?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    t.mode?.toLowerCase().includes(searchQuery.toLowerCase())
  );
  const paginated = filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const createMutation = useMutation({
    mutationFn: (data) => adminCreateTraining(data),
    onSuccess: () => { qc.invalidateQueries(["admin-trainings"]); notify("Training created"); closeDialog(); },
    onError: (err) => notify(err?.response?.data?.message || "Couldn't create training", "error"),
  });
  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => adminUpdateTraining(id, data),
    onSuccess: () => { qc.invalidateQueries(["admin-trainings"]); notify("Training updated"); closeDialog(); },
    onError: (err) => notify(err?.response?.data?.message || "Couldn't update training", "error"),
  });
  const deleteMutation = useMutation({
    mutationFn: (id) => adminDeleteTraining(id),
    onSuccess: () => { qc.invalidateQueries(["admin-trainings"]); notify("Training deleted"); setConfirmDelete(null); },
    onError: (err) => { notify(err?.response?.data?.message || "Couldn't delete training", "error"); setConfirmDelete(null); },
  });
  const statusMutation = useMutation({
    mutationFn: ({ id, isActive }) => adminSetTrainingStatus(id, isActive),
    onSuccess: () => { qc.invalidateQueries(["admin-trainings"]); notify("Status updated"); },
    onError: (err) => notify(err?.response?.data?.message || "Couldn't update status", "error"),
  });

  const saving = createMutation.isPending || updateMutation.isPending;

  const onSubmit = (values) => {
    const payload = {
      ...values,
      slug: values.slug ? slugify(values.slug) : slugify(values.title),
      description: isQuillEmpty(values.description) ? "" : values.description,
      price: values.price === "" ? null : Number(values.price),
      rating: values.rating === "" ? null : Number(values.rating),
      enrollmentCount: values.enrollmentCount === "" ? null : Number(values.enrollmentCount),
      duration: values.duration === "" ? null : values.duration,
      displayOrder: Number(values.displayOrder) || 0,
    };
    // Only attach binary files if the user actually picked one; otherwise the
    // imageUrl/iconUrl text fields above are sent as-is (harmless if the
    // backend ignores them, used directly if it accepts them).
    if (imageFile) payload.image = imageFile;
    if (iconFile) payload.icon = iconFile;

    if (editing) updateMutation.mutate({ id: editing.id, data: payload });
    else createMutation.mutate(payload);
  };

  const openCreate = () => {
    setEditing(null);
    reset(DEFAULT_VALUES);
    setImageFile(null);
    setIconFile(null);
    setOpen(true);
  };

  const openEdit = async (t) => {
    setEditLoading(true);
    try {
      const { data } = await adminGetTrainingById(t.id);
      const record = data?.data ?? data;
      setEditing(record);
      reset({ ...DEFAULT_VALUES, ...record });
      setImageFile(null);
      setIconFile(null);
      setOpen(true);
    } catch (err) {
      notify(err?.response?.data?.message || "Couldn't load training", "error");
    } finally {
      setEditLoading(false);
    }
  };

  const closeDialog = () => {
    if (saving) return;
    setOpen(false); setEditing(null); reset(DEFAULT_VALUES);
    setImageFile(null); setIconFile(null);
  };

  const handleImageFilePick = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (file) setImageFile(file);
  };
  const handleIconFilePick = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (file) setIconFile(file);
  };

  // ---- Excel: template / export / import ----
  const handleDownloadTemplate = async () => {
    try {
      const { data } = await adminDownloadTrainingTemplate();
      downloadBlob(data, "training-import-template.xlsx");
    } catch { notify("Couldn't download template", "error"); }
    setMenuAnchor(null);
  };

  const handleExport = async () => {
    try {
      const { data } = await adminExportTrainings();
      downloadBlob(data, "trainings-export.xlsx");
    } catch { notify("Couldn't export trainings", "error"); }
    setMenuAnchor(null);
  };

  const handleImportClick = () => { fileInputRef.current?.click(); setMenuAnchor(null); };

  const handleImportFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await adminImportTrainings(fd);
      const created = data?.data?.createdCount ?? data?.createdCount;
      qc.invalidateQueries(["admin-trainings"]);
      notify(created != null ? `Imported ${created} training${created === 1 ? "" : "s"}` : "Import complete");
    } catch (err) {
      const rowErrors = err?.response?.data?.errors;
      notify(
        Array.isArray(rowErrors) && rowErrors.length
          ? `Row ${rowErrors[0].row}: ${rowErrors[0].message}`
          : err?.response?.data?.message || "Import failed — check the file format",
        "error"
      );
    } finally {
      setImporting(false);
    }
  };

  return (
    <AdminShell title="Training">
      <Box sx={{
        display: "flex", alignItems: { xs: "flex-start", sm: "center" }, justifyContent: "space-between",
        flexDirection: { xs: "column", sm: "row" }, gap: 1.5, mb: 2.5,
      }}>
        <Typography sx={{ fontSize: 13.5, color: "text.secondary", maxWidth: 460 }}>
          Trainings shown on the public site. Set status to Active to make a training visible to visitors.
        </Typography>
        <Box sx={{ display: "flex", gap: 1, flexShrink: 0 }}>
          <input ref={fileInputRef} type="file" accept=".xlsx" hidden onChange={handleImportFile} />
          <Button
            variant="outlined"
            onClick={(e) => setMenuAnchor(e.currentTarget)}
            startIcon={importing ? <CircularProgress size={16} /> : <MoreVertical size={16} />}
            disabled={importing}
            sx={{ borderRadius: "10px", textTransform: "none", fontWeight: 700, borderColor: "rgba(15,23,42,0.15)", color: "#334155" }}
          >
            {importing ? "Importing…" : "Excel"}
          </Button>
          <Menu anchorEl={menuAnchor} open={!!menuAnchor} onClose={() => setMenuAnchor(null)}>
            <MenuItem onClick={handleDownloadTemplate}><Download size={15} style={{ marginRight: 8 }} /> Download template</MenuItem>
            <MenuItem onClick={handleImportClick}><FileUp size={15} style={{ marginRight: 8 }} /> Import Excel</MenuItem>
            <MenuItem onClick={handleExport}><FileDown size={15} style={{ marginRight: 8 }} /> Export Excel</MenuItem>
          </Menu>
          <Button
            variant="contained" startIcon={<Plus size={18} />} onClick={openCreate}
            sx={{ borderRadius: "10px", textTransform: "none", fontWeight: 700, bgcolor: "#2563EB",
              boxShadow: "0 10px 24px -12px rgba(37,99,235,0.5)", "&:hover": { bgcolor: "#1D4ED8" } }}
          >
            Add Training
          </Button>
        </Box>
      </Box>

      <Card sx={{ borderRadius: "16px", border: "1px solid rgba(15,23,42,0.08)", boxShadow: "0 1px 2px rgba(15,23,42,0.04)", p: 2, mb: 2.5 }}>
        <TextField
          fullWidth placeholder="Search trainings by title, category, or mode…"
          value={searchQuery} onChange={(e) => { setSearchQuery(e.target.value); setPage(0); }}
          InputProps={{ startAdornment: <InputAdornment position="start"><Search size={18} color="#94A3B8" /></InputAdornment> }}
          sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
        />
      </Card>

      <Card sx={{ borderRadius: "16px", border: "1px solid rgba(15,23,42,0.08)", boxShadow: "0 1px 2px rgba(15,23,42,0.04)", overflow: "hidden" }}>
        <Box sx={{ overflowX: "auto" }}>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: "#F8FAFC" }}>
                {["Training", "Mode", "Category", "Price", "Order", "Status", ""].map((h) => (
                  <TableCell key={h} sx={{ fontWeight: 700, fontSize: 12, letterSpacing: "0.03em", textTransform: "uppercase",
                    color: "text.secondary", borderBottom: "1px solid rgba(15,23,42,0.08)", whiteSpace: "nowrap" }}>
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                Array.from({ length: 6 }).map((_, i) => (
                  <TableRow key={i}>{Array.from({ length: 7 }).map((__, j) => (
                    <TableCell key={j}><Skeleton variant="text" width={j === 0 ? 180 : 70} /></TableCell>
                  ))}</TableRow>
                ))
              ) : paginated.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 8 }}>
                    <Box sx={{ width: 52, height: 52, borderRadius: "14px", bgcolor: "rgba(37,99,235,0.07)",
                      display: "flex", alignItems: "center", justifyContent: "center", mx: "auto", mb: 1.5 }}>
                      <GraduationCap size={22} color="#2563EB" />
                    </Box>
                    <Typography fontWeight={700} color="#0F172A" mb={0.5}>No trainings found</Typography>
                    <Typography color="text.secondary" fontSize={13.5}>
                      {searchQuery ? "Try adjusting your search" : "Add your first training or import from Excel."}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : paginated.map((t) => (
                <TableRow key={t.id} hover sx={{ "&:last-child td": { borderBottom: 0 } }}>
                  <TableCell sx={{ minWidth: 240 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                      <Avatar variant="rounded" src={t.imageUrl || undefined}
                        sx={{ width: 38, height: 38, borderRadius: "9px", bgcolor: "rgba(37,99,235,0.08)", color: "#2563EB" }}>
                        <GraduationCap size={16} />
                      </Avatar>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontWeight: 700, fontSize: 13.75, color: "#0F172A" }} noWrap>{t.title}</Typography>
                        {t.featured && (
                          <Chip label="Featured" size="small" sx={{ mt: 0.3, height: 18, fontSize: 10, fontWeight: 700, bgcolor: "#111827", color: "white" }} />
                        )}
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>
                    <Chip label={MODES.find((m) => m.value === t.mode)?.label || t.mode || "—"} size="small"
                      sx={{ fontWeight: 600, fontSize: 11, height: 22, bgcolor: "rgba(37,99,235,0.07)", color: "#2563EB" }} />
                  </TableCell>
                  <TableCell sx={{ fontSize: 13.5, color: "text.secondary", whiteSpace: "nowrap" }}>{t.category || "—"}</TableCell>
                  <TableCell sx={{ fontSize: 13.5, color: "#0F172A", fontWeight: 600, whiteSpace: "nowrap" }}>
                    {t.price != null ? `${t.currency === "USD" ? "$" : "₹"}${Number(t.price).toLocaleString()}` : "—"}
                  </TableCell>
                  <TableCell sx={{ fontSize: 13.5, color: "text.secondary" }}>{t.displayOrder ?? 0}</TableCell>
                  <TableCell>
                    <FormControlLabel
                      control={
                        <Switch
                          size="small"
                          checked={!!t.isActive}
                          onChange={(e) => statusMutation.mutate({ id: t.id, isActive: e.target.checked })}
                        />
                      }
                      label={<Typography sx={{ fontSize: 12, fontWeight: 600, color: t.isActive ? "#16A34A" : "#94A3B8" }}>
                        {t.isActive ? "Active" : "Inactive"}
                      </Typography>}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Box sx={{ display: "flex", gap: 0.75, justifyContent: "flex-end" }}>
                      <Tooltip title="Edit">
                        <span>
                          <IconButton size="small" onClick={() => openEdit(t)} disabled={editLoading}
                            sx={{ border: "1px solid rgba(15,23,42,0.1)", borderRadius: "8px" }}>
                            {editLoading ? <CircularProgress size={14} /> : <Pencil size={14} />}
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton size="small" onClick={() => setConfirmDelete(t)}
                          sx={{ border: "1px solid rgba(15,23,42,0.1)", borderRadius: "8px",
                            "&:hover": { borderColor: "#EF4444", color: "#EF4444" } }}>
                          <Trash2 size={14} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
        {!isLoading && (
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]} component="div" count={filtered.length}
            rowsPerPage={rowsPerPage} page={page}
            onPageChange={(e, p) => setPage(p)}
            onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
            sx={{ borderTop: "1px solid rgba(15,23,42,0.08)" }}
          />
        )}
      </Card>

      {/* Create / Edit dialog */}
      <Dialog open={open} onClose={closeDialog} maxWidth="md" fullWidth
        PaperProps={{ sx: { borderRadius: "24px", maxHeight: "90vh" } }}>
        <DialogTitle sx={{ py: 2.5, px: 3, background: "linear-gradient(135deg, #1D4ED8 0%, #2563EB 55%, #3B82F6 100%)" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.75 }}>
            <Box sx={{ width: 44, height: 44, borderRadius: "12px", bgcolor: "rgba(255,255,255,0.18)",
              display: "flex", alignItems: "center", justifyContent: "center" }}>
              <GraduationCap size={22} color="#FFFFFF" />
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 800, fontSize: 21, color: "#FFFFFF" }}>
                {editing ? "Edit Training" : "Add New Training"}
              </Typography>
              <Typography sx={{ fontSize: 13.5, color: "rgba(255,255,255,0.85)", mt: 0.25 }}>
                {editing ? `Editing: ${editing.title}` : "Create a training for your catalog"}
              </Typography>
            </Box>
          </Box>
        </DialogTitle>

        <Box component="form" onSubmit={handleSubmit(onSubmit)}>
          <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2.5, pt: 3, pb: 1, px: 3,
            maxHeight: "65vh", overflowY: "auto", backgroundColor: "#F1F5F9" }}>

            <Paper elevation={0} sx={{ p: 3, borderRadius: "16px", border: "1px solid rgba(15,23,42,0.06)",
              borderLeft: "4px solid #2563EB", bgcolor: "#FFFFFF" }}>
              <Typography sx={{ fontSize: 15.5, fontWeight: 800, color: "#0F172A", mb: 2.5 }}>Training Details</Typography>
              <Box sx={{ display: "grid", gridTemplateColumns: "repeat(12, 1fr)", gap: 2.5 }}>
                <Box sx={{ gridColumn: { xs: "span 12", sm: "span 8" } }}>
                  <TextField label="Title" fullWidth required disabled={saving}
                    error={!!errors.title} helperText={errors.title ? "Title is required" : ""}
                    InputProps={{ startAdornment: <InputAdornment position="start"><Type size={16} color="#94A3B8" /></InputAdornment> }}
                    InputLabelProps={{ shrink: true }} {...register("title", { required: true })} />
                </Box>
                <Box sx={{ gridColumn: { xs: "span 12", sm: "span 4" } }}>
                  <TextField label="Badge" fullWidth disabled={saving} placeholder="e.g., BESTSELLER"
                    InputLabelProps={{ shrink: true }} {...register("badge")} />
                </Box>
                <Box sx={{ gridColumn: "span 12" }}>
                  <TextField label="Slug" fullWidth disabled={saving} placeholder="auto-generated from title if left blank"
                    InputProps={{ startAdornment: <InputAdornment position="start"><Link2 size={16} color="#94A3B8" /></InputAdornment> }}
                    InputLabelProps={{ shrink: true }} {...register("slug")} />
                </Box>
                <Box sx={{ gridColumn: "span 12" }}>
                  <TextField label="Short Description" fullWidth required disabled={saving}
                    error={!!errors.shortDescription}
                    InputLabelProps={{ shrink: true }} {...register("shortDescription", { required: true })} />
                </Box>
                <Box sx={{ gridColumn: "span 12" }}>
                  <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#475569", mb: 0.75 }}>
                    Full Description
                  </Typography>
                  <Controller
                    name="description"
                    control={control}
                    render={({ field }) => (
                      <Box
                        sx={{
                          bgcolor: "#FFFFFF",
                          borderRadius: "10px",
                          overflow: "hidden",
                          border: "1px solid rgba(0,0,0,0.23)",
                          "& .ql-toolbar": { borderRadius: "10px 10px 0 0", borderColor: "transparent", borderBottom: "1px solid rgba(0,0,0,0.1)" },
                          "& .ql-container": { borderColor: "transparent", fontSize: 14.5, minHeight: 180 },
                          "& .ql-editor": { minHeight: 180 },
                        }}
                      >
                        <ReactQuill
        ref={(el) => {
          if (el && !el.__pasteHandlerAttached) {
            const editor = el.getEditor();
            editor.clipboard.addMatcher(Node.ELEMENT_NODE, (node, delta) => {
              // Drop all inline formatting (color, font, white-space, etc.)
              // from pasted content — keep only plain text runs.
              const Delta = editor.constructor.import("delta");
              const plain = new Delta();
              delta.ops.forEach((op) => {
                if (typeof op.insert === "string") plain.insert(op.insert);
              });
              return plain;
            });
            el.__pasteHandlerAttached = true;
          }
        }}
        theme="snow"
        value={field.value || ""}
        onChange={field.onChange}
        readOnly={saving}
        modules={QUILL_MODULES}
        formats={QUILL_FORMATS}
        placeholder="Write the full training description…"
      />
                      </Box>
                    )}
                  />
                </Box>
              </Box>
            </Paper>

            <Paper elevation={0} sx={{ p: 3, borderRadius: "16px", border: "1px solid rgba(15,23,42,0.06)",
              borderLeft: "4px solid #7C3AED", bgcolor: "#FFFFFF" }}>
              <Typography sx={{ fontSize: 15.5, fontWeight: 800, color: "#0F172A", mb: 2.5 }}>Media & Classification</Typography>
              <Box sx={{ display: "grid", gridTemplateColumns: "repeat(12, 1fr)", gap: 2.5 }}>
                {/* Image: URL field (optional, matches GET response shape) + real file upload (matches POST/PUT/PATCH multipart schema) */}
                <Box sx={{ gridColumn: { xs: "span 12", sm: "span 6" } }}>
                  <TextField label="Image URL" fullWidth disabled={saving}
                    helperText="Optional — or upload a file below"
                    InputProps={{ startAdornment: <InputAdornment position="start"><Upload size={16} color="#94A3B8" /></InputAdornment> }}
                    InputLabelProps={{ shrink: true }} {...register("imageUrl")} />
                  <input ref={imageFileInputRef} type="file" accept="image/*" hidden onChange={handleImageFilePick} />
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.75 }}>
                    <Button size="small" variant="outlined" startIcon={<ImagePlus size={14} />}
                      onClick={() => imageFileInputRef.current?.click()} disabled={saving}
                      sx={{ textTransform: "none", borderRadius: "8px", fontSize: 12.5 }}>
                      {imageFile ? "Replace image file" : "Upload image file"}
                    </Button>
                    {imageFile && (
                      <Chip size="small" label={imageFile.name} onDelete={() => setImageFile(null)}
                        deleteIcon={<X size={13} />} sx={{ fontSize: 11, maxWidth: 180 }} />
                    )}
                  </Box>
                </Box>
                <Box sx={{ gridColumn: { xs: "span 12", sm: "span 6" } }}>
                  <TextField label="Icon URL" fullWidth disabled={saving}
                    helperText="Optional — or upload a file below"
                    InputLabelProps={{ shrink: true }} {...register("iconUrl")} />
                  <input ref={iconFileInputRef} type="file" accept="image/*" hidden onChange={handleIconFilePick} />
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.75 }}>
                    <Button size="small" variant="outlined" startIcon={<ImagePlus size={14} />}
                      onClick={() => iconFileInputRef.current?.click()} disabled={saving}
                      sx={{ textTransform: "none", borderRadius: "8px", fontSize: 12.5 }}>
                      {iconFile ? "Replace icon file" : "Upload icon file"}
                    </Button>
                    {iconFile && (
                      <Chip size="small" label={iconFile.name} onDelete={() => setIconFile(null)}
                        deleteIcon={<X size={13} />} sx={{ fontSize: 11, maxWidth: 180 }} />
                    )}
                  </Box>
                </Box>
                <Box sx={{ gridColumn: { xs: "span 12", sm: "span 4" } }}>
                  <TextField label="Category" fullWidth disabled={saving}
                    InputProps={{ startAdornment: <InputAdornment position="start"><Layers size={16} color="#94A3B8" /></InputAdornment> }}
                    InputLabelProps={{ shrink: true }} {...register("category")} />
                </Box>
                <Box sx={{ gridColumn: { xs: "span 12", sm: "span 4" } }}>
                  <Controller name="mode" control={control} render={({ field }) => (
                    <TextField select label="Mode" fullWidth disabled={saving} InputLabelProps={{ shrink: true }}
                      {...field} value={field.value ?? "classroom"}>
                      {MODES.map((m) => <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>)}
                    </TextField>
                  )} />
                </Box>
                <Box sx={{ gridColumn: { xs: "span 12", sm: "span 4" } }}>
                  <TextField label="Level" fullWidth disabled={saving} placeholder="Beginner / Intermediate / Advanced"
                    InputLabelProps={{ shrink: true }} {...register("level")} />
                </Box>
                <Box sx={{ gridColumn: { xs: "span 12", sm: "span 6" } }}>
                  <TextField label="Duration" fullWidth disabled={saving} placeholder="e.g., 6 weeks"
                    InputProps={{ startAdornment: <InputAdornment position="start"><Clock size={16} color="#94A3B8" /></InputAdornment> }}
                    InputLabelProps={{ shrink: true }} {...register("duration")} />
                </Box>
              </Box>
            </Paper>

            <Paper elevation={0} sx={{ p: 3, borderRadius: "16px", border: "1px solid rgba(15,23,42,0.06)",
              borderLeft: "4px solid #16A34A", bgcolor: "#FFFFFF" }}>
              <Typography sx={{ fontSize: 15.5, fontWeight: 800, color: "#0F172A", mb: 2.5 }}>Pricing & Stats</Typography>
              <Box sx={{ display: "grid", gridTemplateColumns: "repeat(12, 1fr)", gap: 2.5 }}>
                <Box sx={{ gridColumn: { xs: "span 6", sm: "span 3" } }}>
                  <TextField label="Price" fullWidth disabled={saving} type="number" inputProps={{ min: 0 }}
                    InputProps={{ startAdornment: <InputAdornment position="start"><DollarSign size={16} color="#94A3B8" /></InputAdornment> }}
                    InputLabelProps={{ shrink: true }} {...register("price", { min: 0 })} />
                </Box>
                <Box sx={{ gridColumn: { xs: "span 6", sm: "span 3" } }}>
                  <Controller name="currency" control={control} render={({ field }) => (
                    <TextField select label="Currency" fullWidth disabled={saving} InputLabelProps={{ shrink: true }}
                      {...field} value={field.value ?? "INR"}>
                      <MenuItem value="INR">INR</MenuItem>
                      <MenuItem value="USD">USD</MenuItem>
                    </TextField>
                  )} />
                </Box>
                <Box sx={{ gridColumn: { xs: "span 6", sm: "span 3" } }}>
                  <TextField label="Rating" fullWidth disabled={saving} type="number" inputProps={{ min: 0, max: 5, step: 0.1 }}
                    InputProps={{ startAdornment: <InputAdornment position="start"><Star size={16} color="#94A3B8" /></InputAdornment> }}
                    InputLabelProps={{ shrink: true }} {...register("rating", { min: 0, max: 5 })} />
                </Box>
                <Box sx={{ gridColumn: { xs: "span 6", sm: "span 3" } }}>
                  <TextField label="Enrollments" fullWidth disabled={saving} type="number" inputProps={{ min: 0 }}
                    InputProps={{ startAdornment: <InputAdornment position="start"><Users size={16} color="#94A3B8" /></InputAdornment> }}
                    InputLabelProps={{ shrink: true }} {...register("enrollmentCount", { min: 0 })} />
                </Box>
              </Box>
            </Paper>

            <Paper elevation={0} sx={{ p: 3, borderRadius: "16px", border: "1px solid rgba(15,23,42,0.06)",
              borderLeft: "4px solid #0891B2", bgcolor: "#FFFFFF" }}>
              <Typography sx={{ fontSize: 15.5, fontWeight: 800, color: "#0F172A", mb: 2.5 }}>CTA & Display</Typography>
              <Box sx={{ display: "grid", gridTemplateColumns: "repeat(12, 1fr)", gap: 2.5 }}>
                <Box sx={{ gridColumn: { xs: "span 12", sm: "span 6" } }}>
                  <TextField label="CTA Label" fullWidth required disabled={saving} error={!!errors.ctaLabel}
                    InputLabelProps={{ shrink: true }} {...register("ctaLabel", { required: true })} />
                </Box>
                <Box sx={{ gridColumn: { xs: "span 12", sm: "span 6" } }}>
                  <TextField label="CTA URL" fullWidth required disabled={saving} error={!!errors.ctaUrl}
                    InputLabelProps={{ shrink: true }} {...register("ctaUrl", { required: true })} />
                </Box>
                <Box sx={{ gridColumn: { xs: "span 12", sm: "span 4" } }}>
                  <TextField label="Display Order" fullWidth disabled={saving} type="number" inputProps={{ min: 0 }}
                    InputLabelProps={{ shrink: true }} {...register("displayOrder", { min: 0 })} />
                </Box>
                <Box sx={{ gridColumn: { xs: "span 12", sm: "span 4" } }}>
                  <FormControlLabel
                    control={<Switch checked={!!watch("featured")} onChange={(e) => setValue("featured", e.target.checked)} disabled={saving} />}
                    label="Featured" sx={{ mt: 1 }}
                  />
                </Box>
                <Box sx={{ gridColumn: { xs: "span 12", sm: "span 4" } }}>
                  <FormControlLabel
                    control={<Switch checked={!!watch("isActive")} onChange={(e) => setValue("isActive", e.target.checked)} disabled={saving} />}
                    label="Active (visible on site)" sx={{ mt: 1 }}
                  />
                </Box>
              </Box>
            </Paper>
          </DialogContent>

          <DialogActions sx={{ px: 3, py: 2.25, borderTop: "1px solid rgba(15,23,42,0.08)", bgcolor: "#FFFFFF" }}>
            <Button onClick={closeDialog} disabled={saving}
              sx={{ textTransform: "none", fontWeight: 700, color: "#475569", borderRadius: "10px", bgcolor: "rgba(15,23,42,0.04)" }}>
              Cancel
            </Button>
            <Button type="submit" variant="contained" disabled={saving}
              startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
              sx={{ textTransform: "none", fontWeight: 700, borderRadius: "10px",
                background: "linear-gradient(135deg, #2563EB 0%, #1D4ED8 100%)", px: 3.5, minWidth: 140 }}>
              {saving ? "Saving..." : editing ? "Update Training" : "Create Training"}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>

      <Dialog open={!!confirmDelete} onClose={() => !deleteMutation.isPending && setConfirmDelete(null)}
        maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: "18px" } }}>
        <DialogTitle sx={{ pb: 1, borderBottom: "1px solid rgba(15,23,42,0.08)" }}>
          <Typography sx={{ fontWeight: 700, fontSize: 17 }}>Delete this training?</Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Typography sx={{ fontSize: 13.75, color: "text.secondary" }}>
            "{confirmDelete?.title}" will be removed from the public site immediately.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setConfirmDelete(null)} disabled={deleteMutation.isPending}>Cancel</Button>
          <Button onClick={() => deleteMutation.mutate(confirmDelete.id)} disabled={deleteMutation.isPending}
            variant="contained" sx={{ bgcolor: "#EF4444", borderRadius: "10px", "&:hover": { bgcolor: "#DC2626" } }}>
            {deleteMutation.isPending ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={3500} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}>
        <Alert onClose={() => setSnackbar((s) => ({ ...s, open: false }))} severity={snackbar.severity} variant="filled" sx={{ borderRadius: "10px" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </AdminShell>
  );
}